/**
 * SelectionDimLayer — gently shades non-relevant territories while a selected
 * source is waiting for a destination.
 *
 * Each path is drawn independently instead of using a board-sized veil. That
 * keeps the map background and ocean unchanged while the territory fills and
 * raster artwork inside those territories recede.
 *
 * Static: MapView draws it in the selection overlay, which fades in and out as
 * a whole by native view opacity (MapOverlayLayer). An animated prop here would
 * make react-native-svg redraw the overlay every frame.
 */

import React from 'react'
import { Circle, G, Path } from 'react-native-svg'

// Keep non-relevant territories legible while still directing attention to
// the active source/target pair. This is a 12% black tint, just over half the
// previous 22% treatment.
const SELECTION_DIM_OPACITY = 0.12

export interface SelectionDimLayerProps {
  /** Territory ids that should be shaded. */
  territoryIds: readonly string[]
  /** Territory boundary path per id. */
  territoryShapeById: Record<string, string | undefined>
  markerCircles?: { positionById: Record<string, { x: number; y: number } | undefined>; radius: number }
  /** Non-territory art that recedes with the board (the Warfish.net logo). */
  extraShapes?: readonly string[]
}

export function SelectionDimLayer({ territoryIds, territoryShapeById, markerCircles, extraShapes }: SelectionDimLayerProps) {
  if (territoryIds.length === 0) return null
  return (
    <G testID="selection-dim-overlay" pointerEvents="none">
      {territoryIds.map((id) => {
        const shape = territoryShapeById[id]
        if (!shape) return null
        if (!markerCircles) return <Path key={`selection-dim-${id}`} d={shape}
          fill="#000000" fillOpacity={SELECTION_DIM_OPACITY} fillRule="evenodd" pointerEvents="none" />
        return (
          <G key={`selection-dim-${id}`} opacity={SELECTION_DIM_OPACITY} pointerEvents="none">
            {/* Composite the shape and disc once so their overlap does not dim twice. */}
            <Path d={shape} fill="#000000" fillRule="evenodd" pointerEvents="none" />
            {markerCircles?.positionById[id] && <Circle cx={markerCircles.positionById[id]!.x}
              cy={markerCircles.positionById[id]!.y} r={markerCircles.radius} fill="#000000" pointerEvents="none" />}
          </G>
        )
      })}
      {/* One composited group, so overlapping parts (the logo's outline and lettering) dim once. */}
      {extraShapes && extraShapes.length > 0 && <G opacity={SELECTION_DIM_OPACITY} pointerEvents="none">
        {extraShapes.map((shape, i) => <Path key={`selection-dim-extra-${i}`} d={shape} fill="#000000" pointerEvents="none" />)}
      </G>}
    </G>
  )
}
